import { GeneralBlock, GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { cn } from 'src/utils/shadcn';

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { Badge } from 'src/registry/ui/Badge';
import { useIntl } from 'react-intl';

import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';

import { Button } from 'src/registry/ui/Button';
import CreateMembershipAllowedTimeFormModal from 'src/features/allowedTimes/components/forms/membership/CreateMembershipAllowedTimeFormModal';
import { Membership } from 'src/features/membership/types';
import { useGetMembershipConfiguration } from 'src/features/membership/api/useGetMembershipConfiguration';
import { useParking } from 'src/providers/ParkingProvider';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { membershipTypeMessages, zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import MembershipTemplateRequirement, {
    MEMBERSHIP_TEMPLATES_REQUIREMENTS,
} from 'src/features/membershipType/components/MembershipTemplateRequirement';
import CreateMembershipZoneRuleFormModal from 'src/features/membership/components/forms/zoneRule/CreateMembershipZoneRuleFormModal';

type MembershipConfigurationProps = {
    membership: Membership;
};

const MembershipConfiguration = ({ membership }: MembershipConfigurationProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const ref = React.useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = React.useState(true);
    const [height, setHeight] = React.useState(0);

    const { data: membershipConfiguration } = useGetMembershipConfiguration({
        membershipId: membership.id,
        parkingId,
    });

    React.useEffect(() => {
        if (!ref.current || !membershipConfiguration) return;

        const updateHeight = () => {
            setHeight(isOpen ? ref.current!.scrollHeight : 0);
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(ref.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isOpen, membershipConfiguration]);

    if (!membershipConfiguration) return null;

    const isMembershipValid = membershipConfiguration?.valid;

    const generalBlockBackgroundColor = isMembershipValid ? 'bg-green-50' : 'bg-red-50';
    const generalBlockTextColor = isMembershipValid ? 'text-green-700' : 'text-red-700';
    const generalBlockBorderColor = isMembershipValid ? ' border border-green-200' : 'border border-red-200';
    const icon = isMembershipValid ? ICON_MAP.CHECK : ICON_MAP.CIRCLE_EXCLAMATION_SOLID;

    const totalRequiredRecords =
        membershipConfiguration &&
        Object.values(membershipConfiguration).reduce(
            (count, item) => (typeof item === 'object' && item !== null && item.required ? count + 1 : count),
            0,
        );
    const completedRequiredRecords =
        membershipConfiguration &&
        Object.values(membershipConfiguration).reduce(
            (count, item) =>
                typeof item === 'object' && item !== null && item.required && item.valid ? count + 1 : count,
            0,
        );

    const itemsToResolve = totalRequiredRecords - completedRequiredRecords;
    const title = isMembershipValid
        ? membershipMessages.membershipFullyConfigured
        : membershipMessages.membershipNotConfiguredProperly;
    const subTitle = isMembershipValid
        ? formatMessage(membershipMessages.fullyConfiguredDescription)
        : formatMessage(membershipTypeMessages.requiredRecordsToResolve, { count: itemsToResolve });

    const zoneRulesSubTitle = formatMessage(membershipTypeMessages.zoneRulesRequirement, {
        count: membership?.max_allowed_spaces_count ?? 0,
    });

    const zoneRulesRequirementSelectionText = `${formatMessage(membershipTypeMessages.pluralRules, { value: membershipConfiguration?.zone_rules?.zone_rules_count })} - 
    ${formatMessage(membershipTypeMessages.zoneRulesSpaces, { value: `${membershipConfiguration?.zone_rules?.total_spaces_count}/${membership?.max_allowed_spaces_count ?? 0}` })}`;

    if (totalRequiredRecords === 0) return null;

    return (
        <GeneralBlock className={cn('flex-1 gap-0 overflow-hidden p-0', generalBlockBorderColor)}>
            <div
                className={cn(
                    'flex min-h-20 items-center px-4 py-2 lg:min-h-10',
                    generalBlockBackgroundColor,
                    generalBlockTextColor,
                    isMembershipValid && 'cursor-pointer',
                )}
                onClick={() => (isMembershipValid ? setIsOpen((prev) => !prev) : undefined)}
            >
                <span className="grid w-full grid-cols-1 gap-2 text-sm lg:flex lg:flex-row lg:justify-between">
                    <div className="grid grid-cols-1 gap-2 xl:flex xl:flex-row">
                        <div className="flex flex-row gap-2">
                            <FontAwesomeIcon icon={icon} color={generalBlockTextColor} size="lg" />
                            <GeneralBlockTitle className={cn('font-bold', generalBlockTextColor)}>
                                {formatMessage(title)}
                            </GeneralBlockTitle>
                        </div>
                        <GeneralBlockSubtitle className={cn('font-light', generalBlockTextColor)}>
                            {subTitle}
                        </GeneralBlockSubtitle>
                    </div>

                    <Badge
                        theme={'white'}
                        className={cn(
                            'max-h-fit border font-bold opacity-75',
                            isMembershipValid ? 'border-green-400 text-green-700' : 'border-red-400 text-red-700',
                        )}
                    >
                        <div>
                            {formatMessage(membershipTypeMessages.requiredRecords, {
                                count: completedRequiredRecords,
                                totalCount: totalRequiredRecords,
                            })}
                        </div>
                    </Badge>
                </span>
            </div>

            <div
                className="overflow-hidden transition-[height] duration-500 ease-in-out"
                style={{ height: `${height}px` }}
            >
                <div ref={ref} className="px-4 py-4">
                    <div className="flex w-full flex-col justify-start gap-5">
                        {membershipConfiguration.zone_rules.required && (
                            <MembershipTemplateRequirement
                                title={formatMessage(zoneRulesMessages.zoneRules)}
                                subTitle={zoneRulesSubTitle}
                                isRequired={membershipConfiguration.zone_rules?.required}
                                isRequirementComplete={membershipConfiguration.zone_rules?.valid}
                                children={
                                    <CreateMembershipZoneRuleFormModal
                                        children={
                                            <Button variant="destructiveLight" className="cursor-pointer">
                                                {formatMessage(membershipTypeMessages.configure)}
                                                <FontAwesomeIcon icon={ICON_MAP.ARROW_RIGHT} />
                                            </Button>
                                        }
                                    />
                                }
                                requirementSelection={zoneRulesRequirementSelectionText}
                                requirementType={MEMBERSHIP_TEMPLATES_REQUIREMENTS.ZONE_RULES}
                            />
                        )}

                        {membershipConfiguration.allowed_times.required && (
                            <MembershipTemplateRequirement
                                title={formatMessage(allowedTimesMessages.allowedTime)}
                                subTitle={formatMessage(membershipTypeMessages.allowedTimesRequirement)}
                                isRequired={membershipConfiguration.allowed_times?.required}
                                isRequirementComplete={membershipConfiguration.allowed_times?.valid}
                                children={
                                    <CreateMembershipAllowedTimeFormModal
                                        children={
                                            <Button variant="destructiveLight" className="cursor-pointer">
                                                {formatMessage(membershipTypeMessages.configure)}
                                                <FontAwesomeIcon icon={ICON_MAP.ARROW_RIGHT} />
                                            </Button>
                                        }
                                    />
                                }
                                requirementType={MEMBERSHIP_TEMPLATES_REQUIREMENTS.ALLOWED_TIMES}
                                requirementSelection={
                                    membershipConfiguration.allowed_times?.valid
                                        ? formatMessage(membershipTypeMessages.totalAllowedTimes, {
                                              count: membershipConfiguration.allowed_times?.allowed_times_count,
                                          })
                                        : undefined
                                }
                            />
                        )}
                    </div>
                </div>
            </div>
        </GeneralBlock>
    );
};

export default MembershipConfiguration;
